import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  useTheme
} from '@mui/material';
import { ensureReadableOn, getBrandColor, getBrandInk, getBrandSoft } from '../../utils/colorUtils';

/**
 * FinancialSummaryCard — a light, airy summary card with label/value rows.
 *
 * Brand treatment is deliberately restrained: the card surface stays neutral
 * with a soft branded wash, and the tenant colour appears only as a thin top
 * accent and the heading. Filling several of these with a solid brand colour is
 * what previously made the dashboard read as a wall of dark blocks.
 *
 * Monetary values stay semantic. Callers pass display colours tuned for the old
 * dark card (light green, orange); those are re-derived to be readable on the
 * light surface with their hue intact, so "green means collected" still holds
 * without every call site needing to change.
 *
 * Props:
 * - title: Card header text
 * - rows: Array of { label, value, color?, bold? }
 * - footer: Optional { label, value, color } for the bottom summary row
 * - accentColor: Semantic colour for the header accent (defaults to the brand)
 * - variant: "tenant" uses the tenant brand in light mode, "platform" stays platform-fixed
 * - loading: Boolean
 */
const FinancialSummaryCard = ({
  title,
  rows = [],
  footer,
  accentColor,
  variant = 'tenant',
  loading = false
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Dark mode keeps its own controlled elevated surface; light mode gets the
  // soft branded wash. Neither is a solid brand fill.
  const surface = isDark ? theme.palette.background.paper : getBrandSoft(theme, variant);
  const accentBar = getBrandColor(theme, variant);
  const headingColor = isDark ? theme.palette.text.primary : getBrandInk(theme, variant);
  const labelColor = theme.palette.text.secondary;
  const valueColor = theme.palette.text.primary;
  const ruleColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  // Keep a caller's semantic colour, but make it legible on whichever surface
  // this card actually renders on.
  const readable = (color, fallback) =>
    (color ? ensureReadableOn(color, surface, 4.5) : fallback);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="rectangular" height={180} sx={{ mt: 2, borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        backgroundColor: surface,
        borderTop: '3px solid',
        borderTopColor: accentBar
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ pb: 1.25, mb: 1.25, borderBottom: `1px solid ${ruleColor}` }}>
          <Typography variant="subtitle2" sx={{
            fontWeight: 600,
            color: readable(accentColor, headingColor),
            fontSize: '0.8rem',
            letterSpacing: '0.02em'
          }}>
            {title}
          </Typography>
        </Box>

        {/* Rows */}
        <Box sx={{ mb: footer ? 1 : 0 }}>
          {rows.map((row, index) => (
            <Box
              key={row.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.8,
                borderBottom: index < rows.length - 1 ? `1px solid ${ruleColor}` : 'none'
              }}
            >
              <Typography variant="body2" sx={{ color: labelColor, fontSize: '0.8rem' }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{
                fontWeight: row.bold ? 700 : 600,
                fontSize: '0.85rem',
                color: readable(row.color, valueColor)
              }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        {footer && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 1.25,
            mt: 1,
            borderTop: `1px solid ${ruleColor}`
          }}>
            <Typography variant="body2" sx={{
              fontWeight: 600,
              color: labelColor,
              fontSize: '0.8rem'
            }}>
              {footer.label}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: readable(footer.color, valueColor)
              }}
            >
              {footer.value}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialSummaryCard;
